<template>
    <component
        :is="component"
        v-bind="componentAttrs"
        :aria-label="ariaLabel"
        class="CButton btn group"
        :class="buttonClasses"
    >
        <CDotCounter v-if="count" class="absolute -right-2 -top-2" :count="count" />
        <div v-if="hasIcon" class="btn__icon">
            <CSpinner v-if="loading" size="large" />
            <slot v-else name="icon">
                <CIcon
                    :class="{ 'text-red-400': variant === 'favourite', 'mr-1': variant === 'herobutton' }"
                    :name="icon"
                    :title="icon"
                />
            </slot>
        </div>

        <div v-if="$slots.default" class="btn--label" :class="[labelClass]">
            <slot />
        </div>
    </component>
</template>

<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router'

import { type ButtonVariant, type ButtonSize, CButtonSizes } from '@/types/buttons'
import CDotCounter from '@/components/ui/CDotCounter.vue'
import CIcon from '@/components/ui/CIcon.vue'
import CSpinner from '@/components/ui/CSpinner.vue'
import { NuxtLink } from '#components'

const props = defineProps({
    variant: {
        type: String as PropType<ButtonVariant>,
        default: 'default'
    },
    size: {
        type: String as PropType<ButtonSize>,
        default: CButtonSizes.Normal
    },
    iconRight: {
        type: Boolean,
        default: false
    },
    full: {
        type: Boolean,
        default: false
    },
    loading: {
        type: Boolean,
        default: false
    },
    circle: {
        type: Boolean,
        default: false
    },
    icon: {
        type: String,
        default: ''
    },
    alignLeft: {
        type: Boolean,
        default: false
    },
    ariaLabel: {
        type: String,
        default: 'Button'
    },
    labelClass: {
        type: String,
        default: ''
    },
    count: {
        type: Number,
        default: 0
    },
    disabled: {
        type: Boolean,
        default: false
    },
    to: {
        type: [String, Object] as unknown as PropType<RouteLocationRaw>,
        default: undefined
    }
})

const attrs = useAttrs()
const slots = useSlots()

const isNuxtLink = computed(() => Boolean(props.to) && !props.disabled)
const isLink = computed(() => Boolean(attrs.href) && !props.disabled)
const isDisabledLink = computed(() => props.disabled && Boolean(attrs.href || props.to))
const hasIcon = computed(() => Boolean(slots.icon || props.icon || props.loading))

const component = computed(() => {
    if (isNuxtLink.value) return NuxtLink
    if (isLink.value) return 'a'
    return 'button'
})

const componentAttrs = computed(() => {
    const eventHandlers: Record<string, any> = {}
    const attributes: Record<string, any> = {}

    Object.entries(attrs).forEach(([key, value]) => {
        if (key === 'disabled') {
            return
        }

        if (key.startsWith('on')) {
            eventHandlers[key] = value
        } else {
            attributes[key] = value
        }
    })

    // Add the to prop for NuxtLink when it's available
    if (isNuxtLink.value && props.to) {
        return {
            ...attributes,
            ...eventHandlers,
            to: props.to
        }
    }

    // Set type attribute for button elements
    if (!isNuxtLink.value && !isLink.value) {
        return {
            ...attributes,
            ...eventHandlers,
            disabled: props.disabled,
            type: attributes.type || 'button'
        }
    }

    return { ...attributes, ...eventHandlers }
})

const buttonClasses = computed(() => ({
    'btn--link': isLink.value || isNuxtLink.value,
    'btn--block': props.full,
    'btn--large': props.size === 'large',
    'btn--small': props.size === 'small',
    'px-2.5': hasIcon.value,
    'btn--icon-right': props.iconRight && hasIcon.value,
    'px-0 text-left': props.alignLeft,
    'size-20 rounded-full': props.circle,
    'cursor-not-allowed opacity-50': props.disabled,
    'disabled:text-gray-500 disabled:dark:text-txt-body':
        ![
            'primary',
            'primary-outline',
            'primary-150',
            'primary-dark',
            'secondary',
            'pink',
            'link',
            'text',
            'favourite',
            'primary-link'
        ].includes(props.variant) || ['favourite', 'text', 'link', 'primary-link'].includes(props.variant),
    'disabled:bg-gray-200 disabled:dark:bg-subtle': ![
        'primary',
        'primary-outline',
        'primary-150',
        'primary-dark',
        'secondary',
        'pink',
        'link',
        'text',
        'favourite',
        'primary-link',
        'remove',
        'transparent'
    ].includes(props.variant),
    'disabled:bg-primary-100 disabled:text-primary-950': ['primary', 'primary-150', 'primary-outline'].includes(props.variant),
    'disabled:bg-secondary-100 disabled:text-gray-750 dark:disabled:text-gray-750': props.variant === 'secondary',
    'disabled:bg-red-100 disabled:text-red-900': props.variant === 'remove',
    'bg-gray-400 dark:bg-subtle text-black dark:text-txt-headings hover:bg-gray-900 hover:text-white dark:hover:bg-subtle': props.variant === 'default',
    'bg-gray-750 text-white hover:bg-gray-900': props.variant === 'gray-750',
    'bg-primary-910 text-white hover:bg-primary-800 dark:bg-primary-950 dark:hover:bg-primary-950/90': props.variant === 'primary',
    'bg-primary-150 text-primary-950 hover:bg-primary-300': props.variant === 'primary-150',
    'primary-dark': props.variant === 'primary-dark',
    'border border-solid border-primary-910 bg-transparent text-primary-910 hover:bg-primary-910 hover:text-white dark:border-primary-400 dark:text-primary-400 dark:hover:bg-primary-950 dark:hover:text-white':
        props.variant === 'primary-outline',
    'bg-red-830 text-white hover:bg-[#AD326E]': props.variant === 'pink',
    'bg-secondary-500 text-black hover:bg-secondary-800 hover:text-black': props.variant === 'secondary',
    'bg-green-500 text-white hover:bg-green-700 dark:bg-green-800 dark:hover:bg-green-900': props.variant === 'success',
    'bg-red-100 text-red-450 hover:bg-red-200 dark:bg-red-950/40 dark:text-red-300 dark:hover:bg-red-950/60': props.variant === 'decline',
    'bg-green-150 text-green-500 hover:bg-green-200 dark:bg-green-950/40 dark:text-green-300 dark:hover:bg-green-950/60': props.variant === 'success-150',
    'bg-red-500 text-white hover:bg-red-500/80 hover:text-white dark:bg-red-700 dark:hover:bg-red-800': props.variant === 'remove',
    'bg-transparent text-black dark:text-txt-headings hover:underline': props.variant === 'transparent',
    'bg-white dark:bg-surface-card text-black dark:text-txt-headings hover:shadow-dropdown focus:shadow-dropdown': props.variant === 'white',
    'text-white hover:text-white/80 focus:text-white/80': [
        'facebook',
        'google',
        'linkedin',
        'instagram',
        'youtube',
        'vimeo',
        'pinterest',
        'telegram',
        'twitter',
        'whatsapp'
    ].includes(props.variant),
    'border-2 border-solid border-black dark:border-default bg-transparent text-black dark:text-txt-headings hover:border-gray-900 hover:bg-gray-900 hover:text-white dark:hover:border-default dark:hover:bg-subtle':
        props.variant === 'outline',
    'min-h-0 hover:bg-gray-100 hover:dark:bg-subtle hover:disabled:bg-transparent group-hover:bg-gray-100 group-hover:dark:bg-subtle': props.variant === 'reaction',
    'bg-facebook': props.variant === 'facebook',
    'bg-google': props.variant === 'google',
    'bg-linkedin': props.variant === 'linkedin',
    'bg-instagram': props.variant === 'instagram',
    'bg-youtube': props.variant === 'youtube',
    'bg-vimeo': props.variant === 'vimeo',
    'bg-pinterest': props.variant === 'pinterest',
    'bg-telegram': props.variant === 'telegram',
    'bg-twitter': props.variant === 'twitter',
    'bg-whatsapp': props.variant === 'whatsapp',
    'bg-blue-800 text-white hover:text-white/80': props.variant === 'blue-800',
    'bg-blue-900 text-white hover:text-white/80': props.variant === 'blue-900',
    'text-blue-700 dark:text-blue-300 underline hover:no-underline': props.variant === 'link',
    'px-0 text-primary-950 dark:text-primary-400 underline hover:text-primary-800 dark:hover:text-primary-300 hover:no-underline': props.variant === 'primary-link',
    'text-black dark:text-txt-headings': props.variant === 'text' || props.variant === 'favourite',
    'bg-slsp-orange text-white hover:bg-slsp-dark':
        props.variant === 'slsp' || props.variant === 'eduvalue' || props.variant === 'community',
    'mx-2 h-14 bg-[#C8EFF0] text-black dark:text-gray-900 !px-7 hover:bg-primary-300 hover:text-black dark:hover:text-gray-900': props.variant === 'herobutton'
}))
</script>
